import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Custom hook for sharing content with progressive enhancement.
 * Uses native Web Share API on mobile devices (iOS, Android),
 * falls back to clipboard copy on desktop browsers (macOS, Windows, Linux).
 *
 * @returns {Object} Share utilities and state
 * @returns {Function} share - Function to share content (url, title, text)
 * @returns {string} copySuccess - Success/error message to display
 * @returns {boolean} isNativeShareSupported - Whether native share should be used
 */
export const useShare = () => {
    const { t } = useTranslation();
    const [copySuccess, setCopySuccess] = useState('');

    /**
     * Detect if the device is a mobile device (phone or tablet)
     * where native share dialog should be used.
     *
     * Returns true for: iOS (iPhone, iPad, iPod), Android, other mobile devices
     * Returns false for: macOS, Windows, Linux desktop browsers
     */
    const isMobileDevice = useCallback(() => {
        if (typeof navigator === 'undefined') {
            return false;
        }

        // Check user agent for mobile device patterns
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

        if (mobileRegex.test(userAgent)) {
            return true;
        }

        // Special case: iPad on iPadOS 13+ mimics macOS in user agent
        // Detect it using platform and maxTouchPoints
        const isIPad = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 0;
        if (isIPad) {
            return true;
        }

        return false;
    }, []);

    /**
     * Check if native share API is available AND device is mobile
     */
    const isNativeShareSupported = useCallback(() => {
        return typeof navigator !== 'undefined'
            && navigator.share !== undefined
            && isMobileDevice();
    }, [isMobileDevice]);

    /**
     * Share content using native share dialog or clipboard fallback
     * @param {Object} options - Share options
     * @param {string} options.url - URL to share
     * @param {string} [options.title] - Title for native share
     * @param {string} [options.text] - Text description for native share
     * @param {string} [options.successMessage] - Custom success message (defaults to generic message)
     * @param {string} [options.errorMessage] - Custom error message (defaults to generic message)
     */
    const share = useCallback(async ({
        url,
        title = '',
        text = '',
        successMessage = null,
        errorMessage = null
    }) => {
        try {
            if (isNativeShareSupported()) {
                // Use native Web Share API on mobile devices
                await navigator.share({
                    title,
                    text,
                    url
                });
                // Native share succeeded - no message needed (OS provides feedback)
            } else {
                // Fallback to clipboard copy on desktop browsers
                // Include formatted text with URL
                const clipboardContent = text ? `${text}\n${url}` : url;
                await navigator.clipboard.writeText(clipboardContent);
                const message = successMessage || t('common:states.linkCopied');
                setCopySuccess(message);
                setTimeout(() => setCopySuccess(''), 3000);
            }
        } catch (err) {
            // Handle errors (user cancelled share, clipboard denied, etc.)
            console.error('Failed to share:', err);

            // Don't show error if user simply cancelled the share dialog
            if (err.name === 'AbortError') {
                // User cancelled the native share dialog, no error message needed
                return;
            }

            const message = errorMessage || t('common:states.shareFailed');
            setCopySuccess(message);
            setTimeout(() => setCopySuccess(''), 3000);
        }
    }, [t, isNativeShareSupported]);

    return {
        share,
        copySuccess,
        isNativeShareSupported: isNativeShareSupported()
    };
};
